import React, { useState, useRef, useEffect } from 'react';
import { Plus, X, Upload, FileText, AlertCircle, Check } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useMembersForLoanApplication } from '../hooks/useMembers.ts';
import api from '../utils/axios.ts';
import Select from '../components/ui/Select.tsx';

const documentTypes = [
  { value: 'application', label: 'Loan Application Form' },
  { value: 'identity', label: 'Identity Document' },
  { value: 'income_proof', label: 'Income Proof' },
  { value: 'collateral', label: 'Collateral Documentation' },
  { value: 'guarantor', label: 'Guarantor Information' },
  { value: 'other', label: 'Other Documents' },
];

const statusOptions = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'DISAPPROVED', label: 'Disapproved' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'COMPLETED', label: 'Completed' },
];

export default function LoanFormWithDocuments({ initialData, onSubmit, onCancel, members, loanTypes, isLoading, isMemberApply = false, memberId = null }) {
  const [memberIdState, setMemberIdState] = useState(initialData?.member_id || memberId || '');
  const [loanTypeId, setLoanTypeId] = useState(initialData?.loan_type_id || initialData?.loan_type?.id || '');
  const [principal, setPrincipal] = useState(initialData?.principal || '');
  const [startDate, setStartDate] = useState(initialData?.start_date || '');
  const [duration, setDuration] = useState(initialData?.duration || '');
  const [reason, setReason] = useState(initialData?.reason || '');
  const [status, setStatus] = useState(initialData?.status || 'PENDING');
  
  // Document related states
  const [documentRequirements, setDocumentRequirements] = useState(initialData?.document_requirements || '');
  const [documentCategories, setDocumentCategories] = useState(initialData?.document_categories || []);
  const [loanDocuments, setLoanDocuments] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState(initialData?.loan_documents || []);
  const [dragOver, setDragOver] = useState(false);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  // Guarantor related states
  const [selectedGuarantors, setSelectedGuarantors] = useState(initialData?.guarantors || []);
  const [availableGuarantors, setAvailableGuarantors] = useState([]);

  // Get selected member for guarantor calculations
  const selectedMember = members?.find(m => m.id === parseInt(isMemberApply ? memberId : memberIdState));

  // Create member options for react-select
  const memberOptions = members?.map(m => ({ value: m.id, label: `${m.name} (${m.phone_number})` })) || [];

  // Fetch member's contributions for guarantor calculations
  const { data: memberContributions } = useQuery({
    queryKey: ['member-contributions', selectedMember?.id],
    queryFn: async () => {
      if (!selectedMember?.id) return { data: { sum: 0 } };
      const { data } = await api.get('/contributions/statistics', {
        params: { member_id: selectedMember.id }
      });
      return data;
    },
    enabled: !!selectedMember?.id,
  });

  // Fetch all group members for guarantor selection (only when member is applying for loan)
  const { data: groupMembersData } = useMembersForLoanApplication({
    enabled: isMemberApply, // Only fetch when member is applying for loan
  });

  // Use group members for guarantor selection when member is applying, otherwise use the passed members prop
  const availableMembersForGuarantors = isMemberApply ? (groupMembersData?.data || []) : members;


  
  // Get selected loan type
  const selectedLoanType = loanTypes?.find(lt => lt.id === parseInt(loanTypeId));
  
  const fileInputRef = useRef(null);

  // Update form state when initialData changes (for async loading)
  useEffect(() => {
    if (initialData) {
      setMemberIdState(initialData.member_id || memberId || '');
      setLoanTypeId(initialData.loan_type_id || initialData.loan_type?.id || '');
      setPrincipal(initialData.principal || '');
      setStartDate(initialData.start_date || '');
      setDuration(initialData.duration || '');
      setReason(initialData.reason || '');
      setStatus(initialData.status || 'PENDING');
      setExistingDocuments(initialData.loan_documents || []);
      
      // Set document requirements from loan type or existing data
      if (initialData.document_requirements || initialData.document_requirements_description) {
        setDocumentRequirements(initialData.document_requirements || initialData.document_requirements_description || '');
      }
      if (initialData.document_categories || initialData.required_document_categories) {
        setDocumentCategories(initialData.document_categories || initialData.required_document_categories || []);
      }

      // If we have an ID but no loan_documents, fetch the full loan details
      if (initialData.id && !initialData.loan_documents) {
        setIsLoadingDocuments(true);
        api.get(`/memberloan/${initialData.id}`)
          .then(response => {
            if (response.data?.data?.loan_documents) {
              setExistingDocuments(response.data.data.loan_documents);
            }
          })
          .catch(error => {
            console.error('Failed to fetch loan documents:', error);
          })
          .finally(() => {
            setIsLoadingDocuments(false);
          });
      }
    } else if (isMemberApply && memberId) {
      // For member apply, set the member ID
      setMemberIdState(memberId);
    }
  }, [initialData, isMemberApply, memberId]);

  // Auto-set document requirements when loan type changes
  useEffect(() => {
    if (selectedLoanType) {
      if (selectedLoanType.require_documents) {
        // Set document requirements description if provided by loan type
        if (selectedLoanType.document_requirements_description) {
          setDocumentRequirements(selectedLoanType.document_requirements_description);
        }
        
        // Set required document categories if provided by loan type
        if (selectedLoanType.required_document_categories && selectedLoanType.required_document_categories.length > 0) {
          setDocumentCategories(selectedLoanType.required_document_categories);
        }
      } else {
        // Reset document requirements if loan type doesn't require documents
        setDocumentRequirements('');
        setDocumentCategories([]);
      }
    } else {
      // No loan type selected, reset document requirements
      setDocumentRequirements('');
      setDocumentCategories([]);
    }
  }, [selectedLoanType]);

  const addDocument = () => {
    setLoanDocuments([...loanDocuments, {
      id: Date.now(),
      title: '',
      description: '',
      document_type: 'application',
      file: null
    }]);
  };

  const removeDocument = (id) => {
    setLoanDocuments(loanDocuments.filter(doc => doc.id !== id));
  };

  const removeExistingDocument = (id) => {
    setExistingDocuments(existingDocuments.filter(doc => doc.id !== id));
  };

  // Get the list of existing document IDs that should be preserved
  const getExistingDocumentIds = () => {
    return existingDocuments.map(doc => doc.id);
  };

  const updateDocument = (id, field, value) => {
    setLoanDocuments(loanDocuments.map(doc => 
      doc.id === id ? { ...doc, [field]: value } : doc
    ));
  };

  const handleFileSelect = (id, file) => {
    if (file && file.size <= 50 * 1024 * 1024) { // 50MB limit
      updateDocument(id, 'file', file);
      if (!loanDocuments.find(doc => doc.id === id).title) {
        updateDocument(id, 'title', file.name.split('.')[0]);
      }
    } else {
      alert('File size should be less than 50MB');
    }
  };

  const handleDrop = (e, id) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(id, file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const toggleDocumentCategory = (category) => {
    if (documentCategories.includes(category)) {
      setDocumentCategories(documentCategories.filter(cat => cat !== category));
    } else {
      setDocumentCategories([...documentCategories, category]);
    }
  };

  // Guarantor functions
  const addGuarantor = (memberId) => {
    const member = availableMembersForGuarantors.find(m => m.id === parseInt(memberId));
    if (member && !selectedGuarantors.find(g => g.id === member.id)) {
      setSelectedGuarantors([...selectedGuarantors, member]);
    }
  };

  const removeGuarantor = (memberId) => {
    setSelectedGuarantors(selectedGuarantors.filter(g => g.id !== memberId));
  };

  const getAvailableGuarantors = () => {
    // Filter out the loan applicant and already selected guarantors
    const applicantId = isMemberApply ? memberId : memberIdState;
    return availableMembersForGuarantors.filter(member => 
      member.id !== parseInt(applicantId) && 
      !selectedGuarantors.find(g => g.id === member.id)
    );
  };

  // Calculate guarantor requirements based on loan principal and member savings
  const calculateGuarantorRequirements = () => {
    if (!selectedLoanType?.requires_guarantors || !selectedMember || !principal) {
      return null;
    }

    // Get member's total contributions (savings) from API
    const memberSavings = memberContributions?.data?.sum || 0;
    const loanPrincipal = parseFloat(principal) || 0;
    
    if (selectedLoanType.guarantor_threshold_type === 'savings_percentage') {
      const thresholdAmount = memberSavings * selectedLoanType.guarantor_threshold_value;
      const guarantorAmount = Math.max(0, loanPrincipal - thresholdAmount);
      
      return {
        memberSavings,
        loanPrincipal,
        thresholdAmount,
        guarantorAmount,
        needsGuarantor: guarantorAmount > 0,
        thresholdType: 'savings_percentage',
        thresholdPercentage: selectedLoanType.guarantor_threshold_value * 100
      };
    } else if (selectedLoanType.guarantor_threshold_type === 'loan_percentage') {
      const thresholdAmount = loanPrincipal * selectedLoanType.guarantor_threshold_value;
      const guarantorAmount = Math.max(0, loanPrincipal - thresholdAmount);
      
      return {
        memberSavings,
        loanPrincipal,
        thresholdAmount,
        guarantorAmount,
        needsGuarantor: guarantorAmount > 0,
        thresholdType: 'loan_percentage',
        thresholdPercentage: selectedLoanType.guarantor_threshold_value * 100
      };
    }
    
    return null;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const downloadDocument = async (loanId, documentId, fileName) => {
    try {
      const response = await api.get(`/memberloan/${loanId}/documents/${documentId}/download`, {
        responseType: 'blob', // Important for file downloads
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // For editing, if we don't have selectedLoanType, try to find it from initialData
    let currentLoanType = selectedLoanType;
    if (initialData?.id && !currentLoanType && initialData?.loan_type) {
      currentLoanType = loanTypes.find(lt => lt.id === initialData.loan_type.id);
    }
    
    // Validate required documents if loan type requires them (only for new loans or if editing with new documents)
    if (currentLoanType?.require_documents && currentLoanType?.required_document_categories?.length > 0) {
      const uploadedDocumentTypes = loanDocuments
        .filter(doc => doc.file)
        .map(doc => doc.document_type);
      
      // For editing, only validate if there are new documents being uploaded
      const hasNewDocuments = loanDocuments.some(doc => doc.file);
      
      if (!initialData?.id || hasNewDocuments) {
        const missingRequired = currentLoanType.required_document_categories.filter(
          category => !uploadedDocumentTypes.includes(category)
        );
        
        if (missingRequired.length > 0) {
          const missingNames = missingRequired.map(cat => 
            documentTypes.find(dt => dt.value === cat)?.label || cat
          );
          alert(`Please upload documents for the following required categories: ${missingNames.join(', ')}`);
          return;
        }
      }
    }

    // Validate guarantors if loan type requires them and guarantor is actually needed
    if (currentLoanType?.requires_guarantors) {
      const guarantorCalc = calculateGuarantorRequirements();
      if (guarantorCalc && guarantorCalc.needsGuarantor && selectedGuarantors.length === 0) {
        alert(`This loan requires guarantors for KES ${guarantorCalc.guarantorAmount.toLocaleString()}. Please select at least one guarantor.`);
        return;
      }
    }
    
    const formData = new FormData();
    // Only include member_id if it's not a member applying (i.e., admin creating loan for someone else)
    if (!isMemberApply) {
      formData.append('member_id', memberIdState);
    }
    formData.append('loan_type_id', loanTypeId); // Always include loan_type_id
    
    // For edit operations, include the loan_id to identify which loan this is
    if (initialData?.id) {
      formData.append('loan_id', initialData.id);
    }
    
    formData.append('principal', principal);
    formData.append('start_date', startDate);
    // Only include duration if loan type doesn't have a duration set, or if it's an edit operation
    if (!currentLoanType?.duration || !currentLoanType?.duration_unit || initialData?.id) {
      formData.append('duration', duration);
    }
    formData.append('reason', reason);
    formData.append('status', status);
    formData.append('requires_documents', currentLoanType?.require_documents || false);
    formData.append('document_requirements', documentRequirements);
    formData.append('document_categories', JSON.stringify(documentCategories));

    // Add guarantor data if loan type requires guarantors
    if (currentLoanType?.requires_guarantors) {
      formData.append('requires_guarantors', true);
      formData.append('guarantors', JSON.stringify(selectedGuarantors.map(g => g.id)));
    }

    // Add existing documents (for tracking what remains)
    const existingDocumentIds = getExistingDocumentIds();
    if (existingDocumentIds.length > 0) {
      formData.append('existing_documents', JSON.stringify(existingDocumentIds));
      console.log('Preserving existing documents:', existingDocumentIds);
    } else {
      console.log('No existing documents to preserve');
    }

    // Add new loan documents
    const newDocuments = [];
    loanDocuments.forEach((doc, index) => {
      if (doc.file) {
        formData.append(`loan_documents[${index}][file]`, doc.file);
        formData.append(`loan_documents[${index}][title]`, doc.title);
        formData.append(`loan_documents[${index}][description]`, doc.description);
        formData.append(`loan_documents[${index}][document_type]`, doc.document_type);
        newDocuments.push({
          title: doc.title,
          document_type: doc.document_type,
          file_name: doc.file.name
        });
      }
    });

    console.log('Payload summary:', {
      existing_documents: existingDocumentIds,
      new_documents: newDocuments,
      total_existing: existingDocuments.length,
      total_new: newDocuments.length
    });

    onSubmit(formData);
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading loan details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        {initialData ? 'Edit Loan' : (isMemberApply ? 'Apply for Loan' : 'Create New Loan')}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Loan Information */}
        <div className="bg-white p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Loan Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!isMemberApply && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Member *</label>
                <Select
                  options={memberOptions}
                  value={memberOptions.find(option => option.value === parseInt(memberIdState))}
                  onChange={(selectedOption) => setMemberIdState(selectedOption?.value?.toString() || '')}
                  isClearable
                  placeholder="Select member..."
                  classNamePrefix="select"
                  required
                />
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type *</label>
              <select 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500" 
                value={loanTypeId} 
                onChange={e => setLoanTypeId(e.target.value)} 
                required
              >
                <option value="">Select loan type</option>
                {loanTypes?.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Principal Amount *</label>
              <input 
                type="number" 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500" 
                value={principal} 
                onChange={e => setPrincipal(e.target.value)} 
                required 
                min={0}
                placeholder="Enter amount"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input 
                type="date" 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500" 
                value={startDate} 
                onChange={e => setStartDate(e.target.value)} 
                required 
              />
            </div>
            
            {/* Duration field - only show if loan type doesn't have a duration set */}
            {(!selectedLoanType?.duration || !selectedLoanType?.duration_unit) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (months) *</label>
                <input 
                  type="number" 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500" 
                  value={duration} 
                  onChange={e => setDuration(e.target.value)} 
                  required 
                  min={1}
                  placeholder="Number of months"
                />
              </div>
            )}
            
            {/* Show loan type duration info when available */}
            {selectedLoanType?.duration && selectedLoanType?.duration_unit && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Loan Duration:</span> {selectedLoanType.duration} {selectedLoanType.duration_unit === 'months' ? (selectedLoanType.duration > 1 ? 'months' : 'month') : selectedLoanType.duration_unit}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      Duration is automatically set by the loan type
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {!isMemberApply && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500" 
                  value={status} 
                  onChange={e => setStatus(e.target.value)}
                >
                  {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
            )}
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Loan *</label>
            <textarea 
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500" 
              value={reason} 
              onChange={e => setReason(e.target.value)} 
              required
              rows={3}
              placeholder="Describe the purpose of this loan"
            />
          </div>
        </div>

        {/* Guarantor Requirements - Only show when loan type requires guarantors */}
        {selectedLoanType?.requires_guarantors && (
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Guarantor Requirements</h3>
              <span className="text-sm bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                Required by loan type
              </span>
            </div>

            {(() => {
              const guarantorCalc = calculateGuarantorRequirements();
              if (!guarantorCalc) {
                return (
                  <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600">
                      Please select a member and enter loan details to calculate guarantor requirements.
                    </p>
                  </div>
                );
              }

              return (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Member's Savings:</span>
                      <span className="text-sm font-semibold text-gray-800">KES {guarantorCalc.memberSavings.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Loan Principal:</span>
                      <span className="text-sm font-semibold text-gray-800">KES {guarantorCalc.loanPrincipal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        Threshold ({guarantorCalc.thresholdPercentage}% {guarantorCalc.thresholdType === 'savings_percentage' ? 'of savings' : 'of loan'}):
                      </span>
                      <span className="text-sm font-semibold text-gray-800">KES {guarantorCalc.thresholdAmount.toLocaleString()}</span>
                    </div>
                    <hr className="border-gray-300" />
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Guarantor Amount Required:</span>
                      <span className={`text-sm font-bold ${guarantorCalc.needsGuarantor ? 'text-red-600' : 'text-green-600'}`}>
                        {guarantorCalc.needsGuarantor ? `KES ${guarantorCalc.guarantorAmount.toLocaleString()}` : 'No guarantor needed'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}

                          {(() => {
                const guarantorCalc = calculateGuarantorRequirements();
                if (!guarantorCalc || !guarantorCalc.needsGuarantor) {
                  return (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Guarantor Information
                        </label>
                        <div className="text-sm text-gray-600">
                          {guarantorCalc && !guarantorCalc.needsGuarantor ? (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                              <p className="text-green-800 font-medium">✓ No guarantor required for this loan amount</p>
                              <p className="text-green-700 text-sm mt-1">
                                The loan principal (KES {guarantorCalc.loanPrincipal.toLocaleString()}) is within the allowed threshold 
                                (KES {guarantorCalc.thresholdAmount.toLocaleString()}) based on the member's savings.
                              </p>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-600">
                              <p>• Guarantors will be notified after loan submission</p>
                              <p>• They must accept the guarantor request before the loan can be approved</p>
                              <p>• The guarantor amount will be calculated based on the loan type's threshold settings</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Guarantor Information
                      </label>
                      <div className="text-sm text-gray-600 mb-4">
                        <p>• Guarantors will be notified after loan submission</p>
                        <p>• They must accept the guarantor request before the loan can be approved</p>
                        <p>• The guarantor amount will be calculated based on the loan type's threshold settings</p>
                      </div>
                    </div>

                    {/* Guarantor Selection - Only show when guarantor is needed */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Guarantors (Required: KES {guarantorCalc.guarantorAmount.toLocaleString()})
                      </label>
                      <div className="flex gap-2 mb-4">
                        <select 
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
                          onChange={(e) => {
                            if (e.target.value) {
                              addGuarantor(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                        >
                          <option value="">Select a member to add as guarantor</option>
                          {getAvailableGuarantors().map(member => (
                            <option key={member.id} value={member.id}>
                              {member.name} {member.phone ? `(${member.phone})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Selected Guarantors */}
                      {selectedGuarantors.length > 0 && (
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Selected Guarantors ({selectedGuarantors.length})
                          </label>
                          <div className="space-y-2">
                            {selectedGuarantors.map(guarantor => (
                              <div key={guarantor.id} className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                <div>
                                  <span className="font-medium text-gray-700">{guarantor.name}</span>
                                  {guarantor.phone && (
                                    <span className="ml-2 text-sm text-gray-500">({guarantor.phone})</span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeGuarantor(guarantor.id)}
                                  className="text-red-600 hover:text-red-800 p-1"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {getAvailableGuarantors().length === 0 && selectedGuarantors.length === 0 && (
                        <p className="text-sm text-gray-500 italic">
                          No available members to select as guarantors.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}
          </div>
        )}

                {/* Document Requirements - Only show when loan type requires documents */}
        {selectedLoanType?.require_documents && (
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Document Requirements</h3>
              <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                Required by loan type
              </span>
            </div>

            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This loan type requires document attachments. The document requirements and categories below are automatically set based on the loan type configuration.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Required Document Categories
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {documentTypes
                    .filter(type => selectedLoanType?.required_document_categories?.includes(type.value))
                    .map(type => (
                      <label key={type.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={true}
                          disabled={true}
                          className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 opacity-50"
                        />
                        <span className="text-sm text-gray-700 font-medium">
                          {type.label}
                          <span className="ml-1 text-xs text-blue-600 font-medium">(Required)</span>
                        </span>
                      </label>
                    ))}
                </div>
                {(!selectedLoanType?.required_document_categories || selectedLoanType.required_document_categories.length === 0) && (
                  <p className="text-sm text-gray-500 italic">No specific document categories required for this loan type.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Document Uploads - Show when loan type requires documents OR when editing with existing documents */}
        {(selectedLoanType?.require_documents || existingDocuments.length > 0) && (
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">
              Loan Document Attachments
              {(existingDocuments.length > 0 || loanDocuments.length > 0) && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({existingDocuments.length + loanDocuments.length} total)
                </span>
              )}
            </h3>
            <button
              type="button"
              onClick={addDocument}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <Plus size={16} className="mr-1" />
              Add {existingDocuments.length > 0 ? 'New ' : ''}Document
            </button>
          </div>



          {/* Existing Documents */}
          {existingDocuments.length > 0 && (
            <div className="space-y-4 mb-6">
              <h4 className="text-md font-medium text-gray-700 border-b pb-2">Existing Documents</h4>
              {existingDocuments.map((doc, index) => {
                return (
                <div key={`existing-${doc.id}`} className="border border-gray-200 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-700">
                        {doc.title}
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {documentTypes.find(dt => dt.value === doc.document_type)?.label || doc.document_type}
                        </span>
                      </h5>
                      {doc.description && (
                        <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                      )}
                      <div className="flex items-center mt-2 text-sm text-gray-500">
                        <span>{doc.file_name}</span>
                        <span className="mx-2">•</span>
                        <span>{doc.formatted_file_size || `${Math.round(doc.file_size / 1024)} KB`}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeExistingDocument(doc.id)}
                      className="text-red-500 hover:text-red-700 ml-4"
                      title="Remove document"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  
                  <div className="flex items-center bg-green-50 border border-green-200 rounded-lg p-3">
                    <Check size={20} className="text-green-600 mr-2 flex-shrink-0" />
                    <span className="text-sm font-medium text-green-800">Document uploaded</span>
                    <div className="ml-auto flex space-x-2">
                      <button
                        type="button"
                        onClick={() => downloadDocument(initialData?.id, doc.id, doc.file_name)}
                        className="text-blue-600 hover:text-blue-800 text-sm underline bg-transparent border-none cursor-pointer"
                      >
                        View/Download
                      </button>
                      {(doc.file_url || doc.direct_url) && (
                        <a
                          href={doc.direct_url || doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800 text-sm underline"
                          title="Direct link (may require login)"
                        >
                          Direct
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {/* New Documents Section */}
          {isLoadingDocuments ? (
            <div className="text-center py-8 text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>Loading documents...</p>
            </div>
          ) : loanDocuments.length === 0 && existingDocuments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No documents attached yet. Click "Add Document" to upload files.</p>
            </div>
          ) : loanDocuments.length > 0 && (
            <div className="space-y-4">
              {existingDocuments.length > 0 && (
                <h4 className="text-md font-medium text-gray-700 border-b pb-2">New Documents</h4>
              )}
              {loanDocuments.map((doc, index) => (
                <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-medium text-gray-700">Document {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeDocument(doc.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Document Title *</label>
                      <input
                        type="text"
                        value={doc.title}
                        onChange={(e) => updateDocument(doc.id, 'title', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter document title"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                      <select
                        value={doc.document_type}
                        onChange={(e) => updateDocument(doc.id, 'document_type', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        {documentTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={doc.description}
                      onChange={(e) => updateDocument(doc.id, 'description', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={2}
                      placeholder="Optional description"
                    />
                  </div>

                  {/* File Upload Area */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center ${
                      dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                    }`}
                    onDrop={(e) => handleDrop(e, doc.id)}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                  >
                    {doc.file ? (
                      <div className="flex items-center justify-center">
                        <div className="flex items-center bg-green-50 border border-green-200 rounded-lg p-3">
                          <Check size={20} className="text-green-600 mr-2" />
                          <div className="text-left">
                            <p className="text-sm font-medium text-green-800">{doc.file.name}</p>
                            <p className="text-xs text-green-600">{formatFileSize(doc.file.size)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => updateDocument(doc.id, 'file', null)}
                            className="ml-3 text-red-500 hover:text-red-700"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Upload size={32} className="mx-auto mb-4 text-gray-400" />
                        <p className="text-gray-600 mb-2">Drag and drop a file here, or click to select</p>
                        <p className="text-xs text-gray-500">
                          Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, RTF, JPG, PNG, GIF, BMP, ZIP, RAR, CSV (Max: 50MB)
                        </p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.jpg,.jpeg,.png,.gif,.bmp,.zip,.rar,.csv"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleFileSelect(doc.id, file);
                          }}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                          Choose File
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>)}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t">
          <button 
            type="button" 
            className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium"
          >
            {initialData ? 'Update' : 'Create'} Loan
          </button>
        </div>
      </form>
    </div>
  );
} 